import {ElementRef, inject, Injectable, NgZone} from '@angular/core';
import mapboxgl, {LngLat, Marker} from 'mapbox-gl';
import {environment} from '../../../environments/environment';
import {Capacitor} from '@capacitor/core';
import {Geolocation, PermissionStatus, Position} from '@capacitor/geolocation';
import {GlobalService} from '../global/global.service';
import {MapMarkerModel} from "../../models/map/map-marker/map-marker.model";
import {LngLatModel, MapModalReturn} from "../../models/map.models";
import {firstValueFrom, Observable, Subject} from "rxjs";
import {PostService} from "../post/post.service";
import {ModalController} from "@ionic/angular";
import {
  MapSelectLocationModalComponent
} from "../../modal/map-select-location-modal/map-select-location-modal.component";
import {AdditionalModalOptions} from "../../models/general.model";
import {StorageService} from "../storage/storage.service";

@Injectable({providedIn: 'root'})
export class MapService {
  private map?: mapboxgl.Map;
  private resizeObs?: ResizeObserver;
  private pendingResizeTimeout?: any;

  private featureClickCallback?: (point: MapMarkerModel) => void;
  private mapZoom = 14;

  private mapModalComponent?: HTMLIonModalElement;

  // ---- DOM Marker and Clustering ---- //
  private domMarkers: { marker: mapboxgl.Marker; data: MapMarkerModel; el: HTMLDivElement }[] = [];
  private clusterMarkers: mapboxgl.Marker[] = [];
  private clusterRadius = 75;
  private moveListenersAttached = false;
  private activeMarkerId?: number;
  private readonly isBrowser: boolean = false;
  private readonly isMobile: boolean = false;
  private mapModalOpen: boolean = false;

  // Injected services
  private globalService: GlobalService = inject(GlobalService);
  private ngZone: NgZone = inject(NgZone);
  private modalController: ModalController = inject(ModalController);
  private postService: PostService = inject(PostService);
  private storageService: StorageService = inject(StorageService);

  private hoverSubject: Subject<MapMarkerModel | null> = new Subject<MapMarkerModel | null>();
  public hover$: Observable<MapMarkerModel | null> = this.hoverSubject.asObservable();

  private hoverPosSubject: Subject<{ x: number; y: number } | null> = new Subject<{ x: number, y: number } | null>();
  public hoverPos$: Observable<{ x: number; y: number } | null> = this.hoverPosSubject.asObservable();

  private globalHoverElPoa?: HTMLDivElement;
  private globalHoverElMoment?: HTMLDivElement;

  private flyTo$: Subject<{ lng: number; lat: number }> = new Subject<{ lng: number; lat: number }>();
  public flyToPosition$: Observable<{ lng: number; lat: number }> = this.flyTo$.asObservable();

  private lastFetchFn?: () => Promise<MapMarkerModel[]>;
  private lastOnClick?: (p: MapMarkerModel) => void;
  private lastFlyTarget?: { lng: number; lat: number };


  private markersUpdatedSubject = new Subject<void>();
  public markersUpdated$ = this.markersUpdatedSubject.asObservable();

  private paramsLocation: LngLatModel = {lng: 0, lat: 0};

  private selectedMarker: LngLatModel | null = null;

  public setSelectedMarker(coords: LngLatModel | null): void {
    this.selectedMarker = coords;
  }

  public getSelectedMarker(): LngLatModel | null {
    return this.selectedMarker;
  }

  constructor() {
    this.isBrowser = this.globalService.isBrowser;

    if (this.isBrowser) {
      const canHover = window.matchMedia('(hover: hover)').matches;
      this.isMobile = !canHover;
    } else {
      this.isMobile = true;
    }
  }

  public initMap(container: HTMLElement, center: [number, number], zoom = 18, isolated = false): mapboxgl.Map {
    const mapToken =
      Capacitor.getPlatform() === 'web'
        ? environment.mapboxAccessTokenWeb
        : environment.mapboxAccessTokenMobile;

    const map = new mapboxgl.Map({
      accessToken: mapToken,
      container,
      style:'mapbox://styles/dabock/cmiyq3rs4000801s5ak409int',
      center,
      zoom,
      bearing: 0,
      pitch: 60,
      trackResize: true,
      fadeDuration: 0,
      antialias: false,
      failIfMajorPerformanceCaveat: false,
      attributionControl: false,

      renderWorldCopies: true,
      maxTileCacheSize: 4096,
    });

    map.addControl(new mapboxgl.AttributionControl(), 'bottom-left');
    map.on('idle', () => map.resize());

    if (!isolated) {
      this.map = map;

      if (!this.moveListenersAttached) {
        this.moveListenersAttached = true;
      }
    }

    map.on('error', e => {
      if (e?.error?.name === 'AbortError') {
        return;
      }

      console.warn('Mapbox error:', e);
    });

    map.on('zoom', () => this.updateMarkerVisibility(map.getZoom()));

    return map;
  }


  private buildMarkerElement(p: MapMarkerModel): HTMLDivElement {
    const wrapper: HTMLDivElement = document.createElement('div');
    wrapper.className = 'marker-wrapper';

    const el = document.createElement('div');
    el.className = 'marker';

    if (p.type === 'moment') el.classList.add('marker-moment');
    else if (p.type === 'organization') el.classList.add('marker-organization');
    else if (p.type === 'space') el.classList.add('marker-space');
    else el.classList.add('marker-poa');

    if (p.type === 'poa') {
      el.style.backgroundImage = `url('assets/icon/impact_map/poa_icon.svg')`;
    } else {
      el.style.backgroundImage = `url("${p.profile_image}")`;
    }

    wrapper.appendChild(el);

    if (p.type === 'organization' || p.type === 'space') {
      const label: HTMLDivElement = document.createElement('div');
      label.className = 'marker-label';
      label.textContent = p.name ?? '';
      wrapper.appendChild(label);
    }

    return wrapper;
  }

  public addMarkers(points: MapMarkerModel[], onClick?: (point: MapMarkerModel) => void): void {
    if (!this.map) return;
    this.clearMarkers();
    this.featureClickCallback = onClick;

    points.forEach((p: MapMarkerModel) => {
      const el: HTMLDivElement = this.buildMarkerElement(p);
      const marker: Marker = new mapboxgl.Marker(el)
        .setLngLat([p.longitude, p.latitude])
        .addTo(this.map!);

      this.domMarkers.push({marker, data: p, el});

      // Hover-Verhalten je Typ
      if (!this.isMobile) {
        if (p.type === 'poa') this.attachHoverPoa(el, marker, p);
        else if (p.type === 'moment') this.attachHoverMoment(el, marker, p);
        else if (p.type === 'space' || p.type === 'organization') {
          this.attachHoverWithOverlay(el, marker, p);
        }
      }

      // Klick
      el.addEventListener('click', (): void => {
        if (!this.featureClickCallback) return;
        const point: MapMarkerModel = {...p};
        this.ngZone.run(() => this.featureClickCallback!(point));
      });
    });

    this.updateMarkerVisibility(this.map!.getZoom());

    // this.updateClusters();
  }

  async loadAndAddMarkers(fetchPoints: () => Promise<MapMarkerModel[]>, onClick?: (p: MapMarkerModel) => void) {
    this.lastFetchFn = fetchPoints;
    this.lastOnClick = onClick;
    await this.reloadMarkers();
    this.updateMarkerVisibility(this.map!.getZoom());
  }

  // ---- Update clusters ---- //
  private updateClusters(): void {
    if (!this.map) return;

    this.clusterMarkers.forEach((m: mapboxgl.Marker): Marker => m.remove());
    this.clusterMarkers = [];
    this.domMarkers.forEach((m: {
      marker: mapboxgl.Marker;
      data: MapMarkerModel;
      el: HTMLDivElement
    }): string => (m.el.style.display = ''));

    const projected: {
      marker: mapboxgl.Marker;
      data: MapMarkerModel;
      el: HTMLDivElement;
      screenPos: mapboxgl.Point
    }[] = this.domMarkers.map((m: { marker: mapboxgl.Marker; data: MapMarkerModel; el: HTMLDivElement }) => ({
      ...m,
      screenPos: this.map!.project([m.data.longitude, m.data.latitude]),
    }));

    const used = new Set<number>();

    for (let i: number = 0; i < projected.length; i++) {
      if (used.has(i)) continue;
      const base: {
        marker: mapboxgl.Marker;
        data: MapMarkerModel;
        el: HTMLDivElement;
        screenPos: mapboxgl.Point
      } = projected[i];
      const groupIdxs: number[] = [i];

      const maxDist = this.clusterRadius;
      for (let j = i + 1; j < projected.length; j++) {
        if (used.has(j)) continue;
        const cand = projected[j];
        if (Math.abs(base.screenPos.x - cand.screenPos.x) > maxDist) continue;
        if (Math.abs(base.screenPos.y - cand.screenPos.y) > maxDist) continue;
        const dx = base.screenPos.x - cand.screenPos.x;
        const dy = base.screenPos.y - cand.screenPos.y;
        if (dx * dx + dy * dy < maxDist * maxDist) groupIdxs.push(j);
      }


      if (groupIdxs.length > 1) {
        groupIdxs.forEach((idx: number): void => {
          projected[idx].el.style.display = 'none';
          used.add(idx);
        });

        const lng: number = groupIdxs.reduce((sum: number, idx: number): number => sum + projected[idx].data.longitude, 0) / groupIdxs.length;
        const lat: number = groupIdxs.reduce((sum: number, idx: number): number => sum + projected[idx].data.latitude, 0) / groupIdxs.length;

        const clusterEl: HTMLDivElement = this.buildClusterElement(groupIdxs.length, [lng, lat], groupIdxs);
        const clusterMarker: mapboxgl.Marker = new mapboxgl.Marker(clusterEl).setLngLat([lng, lat]).addTo(this.map!);
        this.clusterMarkers.push(clusterMarker);
      } else {
        used.add(i);
      }
    }
  }

  // ---- Build cluster element ---- //
  private buildClusterElement(count: number, lngLat?: [number, number], groupIdxs?: number[]): HTMLDivElement {
    const el: HTMLDivElement = document.createElement('div');
    el.className = 'cluster-marker';
    el.textContent = String(count);

    Object.assign(el.style, {
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      background: 'rgba(0, 55, 61, 0.70)',
      color: '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 'bold',
      cursor: 'pointer'
    });

    el.addEventListener('click', (): void => {
      if (!lngLat || !groupIdxs) return;

      const currentZoom: number = this.map?.getZoom() ?? 0;

      if (currentZoom < 17) {
        this.map?.flyTo({
          center: lngLat,
          zoom: currentZoom + 2,
          speed: 0.4,
          curve: 1.4,
          essential: true
        });
        return;
      }

      const base = this.map!.project(lngLat);
      const angleStep: number = (2 * Math.PI) / groupIdxs.length;

      const baseRadius = 50;
      const radius: number = baseRadius + groupIdxs.length * 5;

      groupIdxs.forEach((idx: number, i: number): void => {
        const angle: number = i * angleStep;
        const offsetX: number = Math.cos(angle) * radius;
        const offsetY: number = Math.sin(angle) * radius;
        const pos = new mapboxgl.Point(base.x + offsetX, base.y + offsetY);
        const lngLatPos: LngLat = this.map!.unproject(pos);

        this.domMarkers[idx].el.style.display = '';
        this.domMarkers[idx].marker.setLngLat(lngLatPos);
      });

      el.style.display = 'none';
    });

    return el;
  }

  public safeResizeSoon(): void {
    if (!this.map) return;
    clearTimeout(this.pendingResizeTimeout);
    this.pendingResizeTimeout = setTimeout(() => {
      requestAnimationFrame(() => this.map?.resize());
    }, 350);
  }

  public teardown(container?: ElementRef<HTMLDivElement>): void {
    clearTimeout(this.pendingResizeTimeout);

    if (this.map) {
      try {
        // this.map.off('moveend', this.updateClusters);
        // this.map.off('zoomend', this.updateClusters);
        // this.map.remove();
        this.map.remove();
      } catch {
      }
      this.map = undefined;
      this.moveListenersAttached = false;
    }

    if (this.resizeObs && container?.nativeElement) {
      try {
        this.resizeObs.unobserve(container.nativeElement);
        this.resizeObs.disconnect();
      } catch {
      }
      this.resizeObs = undefined;
    }
  }

  private whenMapReady(): Promise<void> {
    return new Promise((resolve: (value: (PromiseLike<void> | void)) => void): void => {
      if (!this.map) return resolve();
      const m: any = this.map;
      if (typeof m.loaded === 'function' && m.loaded()) return resolve();
      if (typeof m.isStyleLoaded === 'function' && m.isStyleLoaded()) return resolve();
      m.once('load', (): void => resolve());
    });
  }

  public clearMarkers(): void {
    this.domMarkers.forEach(m => m.marker.remove());
    this.clusterMarkers.forEach(m => m.remove());
    this.domMarkers.length = 0;
    this.clusterMarkers.length = 0;
  }


  public async requestLocation(): Promise<LngLatModel> {
    try {
      // --- Native (Capacitor) ---
      if (!this.globalService.isWeb) {
        this.mapZoom = 14;
        let permission: PermissionStatus = await Geolocation.checkPermissions();
        if (permission.location !== 'granted') {
          permission = await Geolocation.requestPermissions();
        }

        if (permission.location === 'granted') {
          const location: Position = await Geolocation.getCurrentPosition({
            enableHighAccuracy: true,
          });
          return {
            lng: location.coords.longitude,
            lat: location.coords.latitude
          };
        }
      }

      // --- Web (Browser geolocation) ---
      if (this.isBrowser && 'geolocation' in navigator) {
        return new Promise<LngLatModel>((resolve) => {
          const tryOptions = (opts: PositionOptions, next?: () => void) => {
            navigator.geolocation.getCurrentPosition(
              pos => {
                const crd = pos.coords;
                resolve({lng: crd.longitude, lat: crd.latitude});
              },
              async err => {
                console.warn(`Geolocation error (${err.code}): ${err.message}`);
                if (next) {
                  next();
                } else {
                  const ipLoc = await this.getIpLocation();
                  if (ipLoc) {
                    resolve(ipLoc);
                  }
                }
              },
              opts
            );
          };

          tryOptions(
            {enableHighAccuracy: true, timeout: 8000, maximumAge: 60000},
            () => tryOptions({enableHighAccuracy: false, timeout: 6000})
          );
        });
      }

    } catch (err) {
      console.error("ERROR requesting location", err);
    }
  }

  public getMapZoom(): number {
    return this.mapZoom;
  }

  public setActiveMarker(id: number): void {
    this.domMarkers.forEach((m: {
      marker: mapboxgl.Marker;
      data: MapMarkerModel;
      el: HTMLDivElement
    }): void => m.el.querySelector('.marker')?.classList.remove('active'));

    const active: {
      marker: mapboxgl.Marker;
      data: MapMarkerModel;
      el: HTMLDivElement
    } = this.domMarkers.find((m: {
      marker: mapboxgl.Marker;
      data: MapMarkerModel;
      el: HTMLDivElement
    }): boolean => m.data.id === id);
    if (active) {
      active.el.classList.add('selected');
      this.activeMarkerId = id;
    }
  }

  public clearActiveMarker(): void {
    this.domMarkers.forEach(m => {
      m.el.classList.remove('selected');
      m.el.querySelector('.marker')?.classList.remove('active');
    });
    this.activeMarkerId = undefined;
  }

  private attachHoverWithOverlay(
    wrapper: HTMLElement,
    marker: mapboxgl.Marker,
    p: MapMarkerModel,
    offsetX = 175,
    offsetY = 260
  ): void {
    const updatePosition: () => void = (): void => {
      const lngLat = marker.getLngLat();
      const pos = this.map!.project([lngLat.lng, lngLat.lat]);
      this.ngZone.run(() => {
        this.hoverPosSubject.next({x: pos.x - offsetX, y: pos.y - offsetY});
      });
    };

    wrapper.addEventListener('mouseenter', (): void => {
      this.clearHover();
      this.hoverSubject.next(p);
      updatePosition();
      this.map!.on('move', updatePosition);
    });

    wrapper.addEventListener('mouseleave', () => {
      this.handleMouseLeave(undefined, updatePosition);
    });

  }

  private async getIpLocation(): Promise<LngLatModel | null> {
    try {
      const res = await fetch("https://ipapi.co/json/");
      if (!res.ok) return null;
      const data = await res.json();
      if (data && data.latitude && data.longitude) {
        return {lng: data.longitude, lat: data.latitude};
      }
      return null;
    } catch {
      return null;
    }
  }

  private handleMouseLeave(
    hoverElement?: HTMLElement,
    updatePosition?: () => void
  ): void {
    this.clearHover();

    if (hoverElement) {
      hoverElement.style.opacity = '0';
      hoverElement.style.visibility = 'hidden';
    }

    if (updatePosition) {
      this.map?.off('move', updatePosition);
    }
  }

  public clearAllHoverElements(): void {
    if (this.globalHoverElPoa) {
      this.globalHoverElPoa.style.opacity = '0';
      this.globalHoverElPoa.style.visibility = 'hidden';
    }
    if (this.globalHoverElMoment) {
      this.globalHoverElMoment.style.opacity = '0';
      this.globalHoverElMoment.style.visibility = 'hidden';
    }
    this.clearHover();
  }

  private getOrCreateHoverEl(type: 'poa' | 'moment'): HTMLDivElement {
    let target = type === 'poa' ? this.globalHoverElPoa : this.globalHoverElMoment;
    if (!target) {
      target = document.createElement('div');
      target.className = `${type}-hover-element`;
      Object.assign(target.style, {
        position: 'absolute',
        pointerEvents: 'none',
        transition: 'opacity 0.3s ease, visibility 0.3s ease',
        opacity: '0',
        visibility: 'hidden',
        zIndex: '9999',
        border: '4px solid #fff',
        borderRadius: '10px',
        boxShadow: '0 6px 18px rgba(0,0,0,0.25)',
      });

      this.map!.getContainer().appendChild(target);

      if (type === 'poa') {
        Object.assign(target.style, {
          width: '100px',
          height: '100px',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        });
        this.globalHoverElPoa = target;
      } else {
        Object.assign(target.style, {
          width: '80px',
          height: '120px',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        });
        this.globalHoverElMoment = target;
      }
    }
    return target;
  }

  private attachHoverPoa(wrapper: HTMLElement, marker: mapboxgl.Marker, p: MapMarkerModel): void {
    const hoverElement: HTMLDivElement = this.getOrCreateHoverEl('poa');

    const updatePosition: () => void = (): void => {
      const lngLat: LngLat = marker.getLngLat();
      const pos = this.map!.project([lngLat.lng, lngLat.lat]);

      const zoom = this.map!.getZoom();
      if (zoom > 12) {
        hoverElement.style.left = `${pos.x - 50}px`;
        hoverElement.style.top = `${pos.y - 100 - 30}px`;
      } else {
        hoverElement.style.left = `${pos.x - 50}px`;
        hoverElement.style.top = `${pos.y - 100 - 20}px`;
      }
    };

    const showHover: (imageUrl: string) => void = (imageUrl: string): void => {
      hoverElement.style.backgroundImage = `url(${imageUrl})`;
      updatePosition();
      hoverElement.style.opacity = '1';
      hoverElement.style.visibility = 'visible';
      this.map!.on('move', updatePosition);
    };

    const hideHover: () => void = (): void => {
      hoverElement.style.opacity = '0';
      hoverElement.style.visibility = 'hidden';
      this.map!.off('move', updatePosition);
    };

    wrapper.addEventListener('mouseenter', (): void => {
      const fallback = 'assets/images/placeholder.jpg';
      const imageUrl: string = p.profile_image || fallback;

      const img = new Image();
      img.onload = (): void => showHover(imageUrl);
      img.onerror = (): void => showHover(fallback);
      img.src = imageUrl;
    });

    wrapper.addEventListener('mouseleave', hideHover);
  }


  private attachHoverMoment(wrapper: HTMLElement, marker: mapboxgl.Marker, p: MapMarkerModel): void {
    const hoverElement: HTMLDivElement = this.getOrCreateHoverEl('moment');
    const updatePosition: () => void = (): void => {
      const lngLat: LngLat = marker.getLngLat();
      const pos = this.map!.project([lngLat.lng, lngLat.lat]);
      const zoom = this.map!.getZoom();

      if (zoom > 12) {
        hoverElement.style.left = `${pos.x - 40}px`;
        hoverElement.style.top = `${pos.y - 120 - 40}px`;
      } else {
        hoverElement.style.left = `${pos.x - 40}px`;
        hoverElement.style.top = `${pos.y - 120 - 30}px`;
      }
    };

    wrapper.addEventListener('mouseenter', () => {
      hoverElement.style.backgroundImage = p.profile_image
        ? `url(${p.profile_image})`
        : `url('assets/images/placeholder.jpg')`;
      updatePosition();
      hoverElement.style.opacity = '1';
      hoverElement.style.visibility = 'visible';
      this.map!.on('move', updatePosition);
    });

    wrapper.addEventListener('mouseleave', () => {
      hoverElement.style.opacity = '0';
      hoverElement.style.visibility = 'hidden';
      this.map!.off('move', updatePosition);
    });
  }

  public clearHover(): void {
    this.hoverSubject.next(null);
    this.hoverPosSubject.next(null);
  }

  public async createMapModal(
    long: string,
    lat: string,
    editable: boolean,
    type?: string,
    id?: string | number,
    initialCreation: boolean = false
  ): Promise<HTMLIonModalElement> {

    const locationPromise: LngLatModel = await this.requestLocation();

    const marker: LngLatModel = {lng: Number(long), lat: Number(lat)};

    const additionalMobileConf = {
      breakpoints: [0, 1],
      backdropBreakpoint: 0,
      initialBreakpoint: 1,
      backdropDismiss: false,
    };
    const additionalWebConf = {
      id: 'inline-modal',
      cssClass: 'modal-gallery',
      backdropDismiss: true,
    };

    const additionalConf: AdditionalModalOptions =
      this.globalService.isWeb ? additionalWebConf : additionalMobileConf;

    return this.modalController.create({
      component: MapSelectLocationModalComponent,
      componentProps: {
        type,
        id,
        position: locationPromise,
        markerSet: marker,
        editable,
        initialCreation
      },
      backdropDismiss: false,
      canDismiss: async (_data: any, role: string): Promise<boolean> => role !== 'gesture',
      mode: 'ios',
      handle: false,
      ...additionalConf
    });
  }


  public async openMapModal(
    long: string,
    lat: string,
    editable: boolean,
    type?: string,
    id?: string | number,
    initialCreation: boolean = false
  ): Promise<MapModalReturn> {
    if (!this.mapModalOpen) {
      this.mapModalOpen = true;
      const modal = await this.createMapModal(long, lat, editable, type, id, initialCreation);

      let reload: boolean = false;
      let lngLat: LngLatModel = null;
      let replaceUrl: boolean = false;

      await modal.present();

      await modal.onDidDismiss().then(async (result): Promise<void> => {
        this.selectedMarker = null;
        if (result.role === 'submit') {
          if (result?.data) {
            const setCoordinates: LngLatModel = result.data as LngLatModel;
            lngLat = setCoordinates;

            if (id) {
              await firstValueFrom(this.postService.setLocationForPost(setCoordinates, type, String(id)));
            }
          } else {
            if (id) {
              await firstValueFrom(this.postService.removeLocationForPost(type, String(id)));
            }
          }
          reload = true;
        }
        if (result.role === 'showOnMap') {
          replaceUrl = true;
        }
        this.notifyMarkersUpdated();
        this.mapModalOpen = false;
      });

      return {reload, lngLat, replaceUrl};
    }

  }

  public triggerFlyTo(lng: number, lat: number): void {
    this.lastFlyTarget = {lng, lat};
    this.flyTo$.next({lng, lat});
  }

  public consumeLastFlyTarget(): { lng: number; lat: number } | undefined {
    const target: { lng: number; lat: number } = this.lastFlyTarget;
    this.lastFlyTarget = undefined;
    return target;
  }

  public async reloadMarkers(): Promise<void> {
    if (!this.lastFetchFn) return;
    await this.whenMapReady();
    this.clearMarkers();
    const points = await this.lastFetchFn();
    this.addMarkers(points, this.lastOnClick);
    this.map?.triggerRepaint?.();
  }

  public notifyMarkersUpdated(): void {
    this.markersUpdatedSubject.next();
  }

  public async resolveInitialLocationWithParams(
    params?: { lng?: string; lat?: string },
    defaultLoc: LngLatModel = {lng: 10.0, lat: 50.0}
  ): Promise<LngLatModel> {

    await this.storageService.ensureReady();
    let location: LngLatModel | null = null;

    if (typeof window !== 'undefined') {
      const search = new URLSearchParams(window.location.search);
      if (window.location.search.includes('lng') && window.location.search.includes('lat')) {
        const lngQ = Number(search.get('lng'));
        const latQ = Number(search.get('lat'));
        if (!isNaN(lngQ) && !isNaN(latQ)) {
          location = {lng: lngQ, lat: latQ};
        }
      }
    }

    if (!location && params?.lng && params?.lat) {
      const lng = Number(params.lng);
      const lat = Number(params.lat);
      if (!isNaN(lng) && !isNaN(lat)) {
        location = {lng, lat};
      }
    }

    if (!location) {
      const stored = await this.storageService.getItem<LngLatModel>('initLngLat');
      if (stored?.lng && stored?.lat) {
        location = stored;
      }
    }

    if (!location) {
      try {
        const loc = await this.requestLocation();
        if (loc?.lng && loc?.lat) {
          location = loc;
        }
      } catch (e) {
      }
    }

    if (!location || !location.lng || !location.lat) {
      location = defaultLoc;
    }

    if (location.lng !== 0 && location.lat !== 0) {
      // one day
      // const oneDayMs = 24 * 60 * 60 * 1000;

      // one hour
      const oneHourMs = 60 * 60 * 1000; // 1 hour

      await this.storageService.setItem('initLngLat', location, oneHourMs);
    }


    this.paramsLocation = location;
    return location;
  }


  private getMarkerScale(zoom: number): number {
    const minZoom = 8;
    const maxZoom = 18;
    const minScale = 0.4;
    const maxScale = 1.0;

    if (zoom <= minZoom) return minScale;
    if (zoom >= maxZoom) return maxScale;

    return minScale + (zoom - minZoom) * (maxScale - minScale) / (maxZoom - minZoom);
  }

  private updateMarkerVisibility(zoom: number): void {
    const scale = this.getMarkerScale(zoom);

    this.domMarkers.forEach(m => {
      const markerEl = m.el.querySelector('.marker') as HTMLElement;
      if (markerEl) {
        markerEl.style.transform = `scale(${scale})`;
        markerEl.style.transformOrigin = 'center center';
      }
    });
  }


}
